import { format, getTime, formatDistanceToNow } from 'date-fns';
import dayjs from 'dayjs';

// ----------------------------------------------------------------------

type InputValue = Date | string | number | null | undefined;

/**
 * Helper to normalize the input value to a Date object,
 * assuming UTC if the string lacks timezone info.
 */
function parseDate(date: InputValue): Date | null {
  if (!date) return null;

  if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+')) {
    // Assume UTC if no timezone is present
    return new Date(`${date}Z`);
  }

  return new Date(date);
}

export function fDate(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy';
  const parsed = parseDate(date);
  return parsed ? format(parsed, fm) : '';
}

export function fTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'p';
  const parsed = parseDate(date);
  return parsed ? format(parsed, fm) : '';
}

export function fDateTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy p';
  const parsed = parseDate(date);
  return parsed ? format(parsed, fm) : '';
}

export function fTimestamp(date: InputValue) {
  const parsed = parseDate(date);
  return parsed ? getTime(parsed) : '';
}

export function fToNow(date: InputValue) {
  const d = date ? new Date(date) : null;

  if (d) {
    const diff = Date.now() - d.getTime();

    if (diff < 60 * 1000) {
      return 'just now';
    }

    return formatDistanceToNow(d, { addSuffix: true });
  }

  return '';
}

export function isBetween(inputDate: Date | string | number, startDate: Date, endDate: Date) {
  const date = parseDate(inputDate);
  if (!date) return false;

  return (
    new Date(date.toDateString()) >= new Date(startDate.toDateString()) &&
    new Date(date.toDateString()) <= new Date(endDate.toDateString())
  );
}

export function isAfter(startDate: Date | null, endDate: Date | null) {
  const parsedStart = parseDate(startDate);
  const parsedEnd = parseDate(endDate);

  return parsedStart && parsedEnd ? parsedStart.getTime() > parsedEnd.getTime() : false;
}

export const formatDate = (date: string | Date) => {
  return dayjs(date).format('DD.MM.YYYY');
};
